import { BrandLogo, Button, useMediaQuery } from '@catering/ui';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import type { JSX } from 'react';

import { useLogout } from '../auth/use-logout';
import { useSessionStore } from '../auth/session-store';
import { useCurrentUser } from '../auth/use-current-user';
import { hasOrderPermission } from '../orders/order-permissions';
import { BottomTabBar } from './bottom-tab-bar';
import { ProtectedRoute } from './protected-route';
import { usePortalMe } from './use-portal-me';

/**
 * Mobile-first on purpose: `client-portal/proposal.md` — the portal is
 * used from a phone in a warehouse office, not a desk. A single column,
 * no sidebar, matches that reality; a wider viewport still reads fine,
 * it just does not need a second layout to get there.
 */
export function AppShell(): JSX.Element {
  const navigate = useNavigate();
  const logout = useLogout();
  const session = useSessionStore((state) => state.session);
  // Same query as `ProtectedRoute`'s: TanStack Query dedupes by key, so
  // this never doubles the network call.
  const me = usePortalMe(session !== null);
  const currentUser = useCurrentUser();
  const canManageTemplates = hasOrderPermission(
    currentUser.data?.roleCodes ?? [],
    'order.template.manage',
  );
  // design.md §8.2/§10: 640px is the portal's own table-vs-tarjeta cut,
  // distinct from web-admin's 768px sidebar cut — only one of nav/tab
  // bar mounts at a time so `aria-label="Navegación del portal"` never
  // duplicates as a landmark.
  const isDesktop = useMediaQuery('(min-width: 640px)');

  async function handleLogout(): Promise<void> {
    await logout.mutateAsync();
    await navigate({ to: '/login' });
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2.5">
          <BrandLogo size="md" className="shrink-0" />
          <div className="flex flex-col">
            {me.data?.status === 'ACTIVE' && me.data.customer !== null ? (
              <span className="text-xs text-slate-500">{me.data.customer.tradeName}</span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {currentUser.data !== undefined ? (
            <span className="max-w-[8rem] truncate text-sm text-slate-600 sm:max-w-none">
              {currentUser.data.fullName}
            </span>
          ) : null}
          <Button variant="ghost" onClick={() => void handleLogout()} loading={logout.isPending}>
            Salir
          </Button>
        </div>
      </header>

      {isDesktop ? (
        <nav
          aria-label="Navegación del portal"
          className="flex gap-4 overflow-x-auto border-b border-slate-200 bg-white px-4 py-2 text-sm"
        >
          <Link to="/orders" className="whitespace-nowrap text-slate-700 hover:text-slate-900">
            Pedidos
          </Link>
          <Link
            to="/orders/history"
            className="whitespace-nowrap text-slate-700 hover:text-slate-900"
          >
            Historial
          </Link>
          <Link
            to="/portal/delivery-notes"
            className="whitespace-nowrap text-slate-700 hover:text-slate-900"
          >
            Remitos
          </Link>
          {canManageTemplates ? (
            <Link
              to="/orders/templates"
              className="whitespace-nowrap text-slate-700 hover:text-slate-900"
            >
              Plantillas
            </Link>
          ) : null}
          <Link to="/help" className="whitespace-nowrap text-slate-700 hover:text-slate-900">
            Ayuda
          </Link>
        </nav>
      ) : null}

      <main className={`flex-1 p-4 ${isDesktop ? '' : 'pb-20'}`}>
        <ProtectedRoute>
          <Outlet />
        </ProtectedRoute>
      </main>

      {isDesktop ? null : <BottomTabBar />}
    </div>
  );
}
